import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import ClickAwayListener from "react-click-away-listener";
import {
  formatTime,
  generateTimeList,
  parseTimeToDate,
} from "../../utils/helpers";
import { CaretDown } from "../icon/downcaret";
import { TextInput } from "./textInput";

const timeList = generateTimeList(15);

const TimeOptions = ({
  setDate = () => {},
  timeList,
  open,
  time,
  close,
}: {
  setDate?: (time: Date | null) => void;
  timeList: {
    label: string;
    value: Date;
  }[];
  open: boolean;
  time?: Date | null;
  close: () => void;
}) => {
  const optionsRefMap = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (!open) {
      optionsRefMap.current = [];
      return;
    }

    // Instant transition on open
    const selectedIndex = timeList.findIndex(
      (item) => JSON.stringify(item.value) === JSON.stringify(time),
    );

    const frame = requestAnimationFrame(() => {
      const selectedOption = optionsRefMap.current[selectedIndex];

      if (selectedOption) {
        selectedOption.scrollIntoView({
          behavior: "instant",
          block: "center",
        });
      }
    });

    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line
  }, [open]);

  return (
    <div
      className={classNames(
        "absolute left-0 right-0 top-[calc(100%+8px)]",
        "!z-[900] flex w-full flex-col overflow-x-hidden overflow-y-scroll",
        "rounded-md border bg-white",
        "outline-none focus-within:outline-none focus:outline-none",
        "!max-h-[200px]",
      )}
    >
      {timeList.map((item, index) => {
        return (
          <button
            type="button"
            onClick={() => {
              setDate(item.value);
              close();
            }}
            className={classNames(
              "w-full border-b bg-inherit p-3 text-left text-sm font-medium text-primary transition-all duration-200 last:border-b-0 hover:bg-grey-50",
              {
                "!bg-grey-50 !font-bold":
                  JSON.stringify(item.value) === JSON.stringify(time),
              },
            )}
            ref={(el) => {
              optionsRefMap.current[index] = el;
            }}
            key={`${index}-time`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

export const TimeInput = ({
  placeholder = "",
  errors = "",
  title = "",
  time,
  setDate = () => {},
  onBlur,
  onChange,
  className = "",
  name,
}: {
  placeholder: string;
  name: string;
  errors?: string;
  title: string;
  time?: Date | null;
  onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  setDate?: (time: Date | null) => void;
  className?: string;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false);
      }}
    >
      <div className="relative">
        <div
          onClick={() => {
            setOpen(true);
          }}
          className={classNames("w-full text-left", className)}
        >
          <TextInput
            type="time"
            name={name}
            value={formatTime(time, "HH:mm")}
            placeholder={placeholder}
            onChange={(e) => {
              setDate(parseTimeToDate(e.target.value, "HH:mm"));
            }}
            onBlur={onBlur}
            hasTitle
            title={title}
            errors={errors}
            containerClassName="hidden md:block"
            readOnly={false}
            customIcon={<CaretDown />}
            customIconPosition="right"
          />

          <TextInput
            type="text"
            name={name}
            value={formatTime(time, "h:mm A")}
            placeholder={placeholder}
            onChange={() => {}}
            onBlur={onBlur}
            hasTitle
            title={title}
            errors={errors}
            containerClassName="md:hidden"
            readOnly={true}
            customIcon={<CaretDown />}
            customIconPosition="right"
          />
        </div>

        {open && (
          <TimeOptions
            setDate={setDate}
            timeList={timeList}
            time={time}
            open={open}
            close={() => {
              setOpen(false);
            }}
          />
        )}
      </div>
    </ClickAwayListener>
  );
};
